import React, { useState, useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { useLocalStorage } from 'react-use';
import { useAppDispatch } from 'app/store/store';
import {
  fetchSettingsAction,
  setAuthorized,
  fetchServerInfoAction,
  fetchServerSaasHostAction,
  fetchUserStatusAction,
} from 'app/percona/shared/core/reducers';
import { contextSrv } from 'app/core/services/context_srv';
import { Button, HorizontalGroup, Icon, Modal, useStyles2 } from '@grafana/ui';
import { getStyles } from './PerconaBootstrapper.styles';
import { isPmmAdmin } from '../../helpers/permissions';
import { Messages } from './PerconaBootstrapper.messages';
import { PERCONA_TOUR_FLAG } from 'app/tour/constants';
import getSteps from 'app/tour/steps';

// This component is only responsible for populating the store with Percona's settings initially
export const PerconaBootstrapper = () => {
  const dispatch = useAppDispatch();
  const { setCurrentStep, setIsOpen, setSteps } = useTour();
  const [modalIsOpen, setModalIsOpen] = useState(true);
  const [showTour, setShowTour] = useLocalStorage<boolean>(PERCONA_TOUR_FLAG, true);
  const styles = useStyles2(getStyles);
  const isLoggedIn = !!contextSrv.user.isSignedIn;

  const dismissModal = () => {
    setModalIsOpen(false);
  };

  const finishTour = () => {
    setModalIsOpen(false);
    setShowTour(false);
  };

  const startTour = () => {
    setModalIsOpen(false);
    setCurrentStep(0);
    setIsOpen(true);
  };

  useEffect(() => {
    const getSettings = async () => {
      try {
        const settings = await dispatch(fetchSettingsAction()).unwrap();
        setSteps(getSteps(isPmmAdmin(contextSrv.user), settings));
        dispatch(setAuthorized(true));
      } catch (e) {
        if (e.response?.status === 401) {
          setAuthorized(false);
        }
      }
    };

    const bootstrap = async () => {
      await getSettings();
      await dispatch(fetchUserStatusAction());
      await dispatch(fetchServerInfoAction());
      await dispatch(fetchServerSaasHostAction());
    };

    if (isLoggedIn) {
      bootstrap();
    }
  }, [dispatch, isLoggedIn, setCurrentStep, setIsOpen, setSteps]);

  return isLoggedIn && showTour ? (
    <Modal onDismiss={dismissModal} isOpen={modalIsOpen} title={Messages.title}>
      <div className={styles.iconContainer}>
        <Icon type="mono" name="pmm-logo" className={styles.svg} />
      </div>
      <p>
        <strong>{Messages.pmm}</strong>
        {Messages.pmmIs}
      </p>
      <p>
        {Messages.pmmEnables}
        <ul className={styles.list}>
          <li>{Messages.spotCriticalPerformance}</li>
          <li>{Messages.ensureDbPerformance}</li>
          <li>{Messages.backup}</li>
        </ul>
      </p>
      <p>
        {Messages.moreInfo}
        <a
          href="https://docs.percona.com/percona-monitoring-and-management/index.html"
          target="_blank"
          rel="noreferrer noopener"
          className={styles.docsLink}
          data-testid="pmm-tour-online-docs"
        >
          {Messages.pmmOnlineHelp}
        </a>
        .
      </p>
      <HorizontalGroup justify="center" spacing="md">
        <Button onClick={startTour} size="lg" className={styles.callToAction} data-testid="pmm-tour-start">
          {Messages.startTour}
        </Button>
      </HorizontalGroup>
      <HorizontalGroup justify="flex-end" spacing="md">
        <Button variant="secondary" onClick={finishTour} data-testid="pmm-tour-skip">
          {Messages.skip}
        </Button>
        <Button variant="secondary" onClick={() => setModalIsOpen(false)} data-testid="pmm-tour-check-later">
          {Messages.checkLater}
        </Button>
      </HorizontalGroup>
    </Modal>
  ) : (
    <></>
  );
};
